import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, getToken } from "../api";
import type { BackupArchive, BackupPolicy, PanelUser } from "../types";

export function BackupsPage({
  accounts,
  currentUser,
  onRefreshAccounts
}: {
  accounts: PanelUser[];
  currentUser: PanelUser;
  onRefreshAccounts: () => Promise<void>;
}) {
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const reseller = currentUser.role === "reseller";
  const operator = staff || reseller;
  const provisioned = accounts.filter(
    (a) => a.role === "user" && a.provisionStatus === "ready"
  );
  const [userId, setUserId] = useState(
    staff
      ? ""
      : reseller
        ? provisioned[0]?.id || ""
        : currentUser.provisionStatus === "ready"
          ? currentUser.id
          : ""
  );
  const [archives, setArchives] = useState<BackupArchive[]>([]);
  const [policies, setPolicies] = useState<BackupPolicy[]>([]);
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const [label, setLabel] = useState("");
  const [incHome, setIncHome] = useState(true);
  const [incDb, setIncDb] = useState(true);
  const [incMail, setIncMail] = useState(true);
  const [incDns, setIncDns] = useState(true);
  const [storage, setStorage] = useState<"local" | "s3" | "sftp">("local");
  const [encryptPassword, setEncryptPassword] = useState("");

  const [restoreId, setRestoreId] = useState("");
  const [restHome, setRestHome] = useState(true);
  const [restDb, setRestDb] = useState(false);
  const [restMail, setRestMail] = useState(false);
  const [restDns, setRestDns] = useState(false);
  const [decryptPassword, setDecryptPassword] = useState("");

  const [polSchedule, setPolSchedule] = useState<"daily" | "weekly" | "manual">(
    "daily"
  );
  const [polRetention, setPolRetention] = useState(7);

  const load = useCallback(async () => {
    if (!userId && !staff) return;
    setError("");
    try {
      const q = userId ? `?userId=${encodeURIComponent(userId)}` : "";
      const a = await api<{ items: BackupArchive[] }>(`/api/v1/backups${q}`);
      setArchives(a.items || []);
      const p = await api<{ items: BackupPolicy[] }>(
        `/api/v1/backups/policies${q}`
      );
      setPolicies(p.items || []);
      const s = await api<{ status: Record<string, unknown> }>(
        "/api/v1/backups/status"
      );
      setStatus(s.status || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [userId, staff]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!staff && !userId && provisioned[0]?.id) setUserId(provisioned[0].id);
  }, [provisioned, userId, staff]);

  const createBackup = async (e: FormEvent) => {
    e.preventDefault();
    if (!userId) {
      setError("Select an account to back up");
      return;
    }
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await api("/api/v1/backups", {
        method: "POST",
        body: JSON.stringify({
          userId,
          label: label || undefined,
          includes: {
            home: incHome,
            databases: incDb,
            mail: incMail,
            dns: incDns
          },
          storage,
          encryptPassword: encryptPassword || undefined
        })
      });
      setMsg("Backup completed");
      setLabel("");
      setEncryptPassword("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const restore = async (e: FormEvent) => {
    e.preventDefault();
    if (!restoreId) return;
    if (
      !confirm(
        "Restore will overwrite selected components on the account. Continue?"
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      await api(`/api/v1/backups/${restoreId}/restore`, {
        method: "POST",
        body: JSON.stringify({
          components: {
            home: restHome,
            databases: restDb,
            mail: restMail,
            dns: restDns
          },
          decryptPassword: decryptPassword || undefined
        })
      });
      setMsg("Restore completed");
      setDecryptPassword("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const download = async (id: string, name: string) => {
    setError("");
    try {
      const token = getToken();
      const res = await fetch(`/api/v1/backups/${id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/gzip") || ct.includes("octet-stream")) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = name.endsWith(".gz") || name.endsWith(".enc")
          ? name
          : `${name}.tar.gz`;
        a.click();
        URL.revokeObjectURL(url);
        setMsg("Download started");
        return;
      }
      const data = (await res.json()) as {
        ok?: boolean;
        note?: string;
        path?: string;
        error?: { message: string };
      };
      if (data.path) {
        setMsg(`Large archive on agent: ${data.path}`);
      } else {
        setError(data.error?.message || data.note || "Download failed");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const savePolicy = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await api("/api/v1/backups/policies", {
        method: "POST",
        body: JSON.stringify({
          userId,
          name: `${polSchedule} policy`,
          schedule: polSchedule,
          retentionCount: polRetention,
          retentionDays: 30,
          includes: {
            home: incHome,
            databases: incDb,
            mail: incMail,
            dns: incDns
          },
          storage,
          enabled: polSchedule !== "manual"
        })
      });
      setMsg("Backup policy saved");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!userId && !staff) {
    return (
      <>
        <div className="topbar">
          <div>
            <h1>Backup</h1>
            <p>Your account must be ready before backups are available.</p>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void onRefreshAccounts()}
          >
            Refresh
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Backup Configuration</h1>
          <p>
            {staff
              ? "WHM · create / schedule / restore account backups (home, DB, mail, DNS)"
              : "Create backups, download archives, and restore your account"}
          </p>
        </div>
        <div className="topbar-actions">
          {operator ? (
            <select
              className="inline-select"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              {staff ? <option value="">All accounts</option> : null}
              {provisioned.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                  {a.primaryDomain ? ` · ${a.primaryDomain}` : ""}
                </option>
              ))}
            </select>
          ) : null}
          {staff ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={async () => {
                setError("");
                try {
                  const r = await api<{ ran?: number; ok?: boolean }>(
                    "/api/v1/backups/run-scheduled",
                    { method: "POST", body: "{}" }
                  );
                  setMsg(
                    `Scheduled policies run${r.ran != null ? ` (${r.ran})` : ""}`
                  );
                  await load();
                } catch (err) {
                  setError(err instanceof Error ? err.message : String(err));
                }
              }}
            >
              Run due policies
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void load()}
          >
            Refresh
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="whm-stats" style={{ marginBottom: 14 }}>
        <div className="whm-stat">
          <span className="whm-stat-label">Archives</span>
          <span className="whm-stat-value">{archives.length}</span>
          <span className="whm-stat-meta">
            {userId ? "Selected account" : "Server-wide"}
          </span>
        </div>
        <div className="whm-stat">
          <span className="whm-stat-label">Policies</span>
          <span className="whm-stat-value">{policies.length}</span>
          <span className="whm-stat-meta">Scheduled backups</span>
        </div>
        <div className="whm-stat">
          <span className="whm-stat-label">Storage</span>
          <span className="whm-stat-value" style={{ fontSize: 16 }}>
            local
          </span>
          <span className="whm-stat-meta">
            {status?.tarAvailable ? "tar ok" : "tar?"}
            {status?.awsCliAvailable ? " · s3 ready" : ""}
          </span>
        </div>
        <div className="whm-stat">
          <span className="whm-stat-label">Tools</span>
          <div className="row-actions" style={{ marginTop: 6, flexWrap: "wrap" }}>
            <span className="chip">Create</span>
            <span className="chip">Restore</span>
            <span className="chip">Download</span>
            <span className="chip">Schedule</span>
          </div>
        </div>
      </div>

      {status ? (
        <div className="card full" style={{ marginBottom: 16 }}>
          <div className="card-label">Backup engine</div>
          <p className="card-meta">
            <span className="mono">{String(status.backupRoot || "")}</span>
            {" · "}
            {String(status.archiveCount || 0)} on disk
            {" · "}
            tar {status.tarAvailable ? "ok" : "missing"}
            {status.awsCliAvailable ? " · aws cli" : ""}
            {status.scpAvailable ? " · scp" : ""}
          </p>
          <p className="card-meta faint">{String(status.note || "")}</p>
        </div>
      ) : null}

      <form className="panel-form" onSubmit={(e) => void createBackup(e)}>
        <div className="panel-form-title">Create a New Backup</div>
        <div className="form-grid">
          <div className="field">
            <label>Label</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="optional"
            />
          </div>
          <div className="field">
            <label>Storage</label>
            <select
              value={storage}
              onChange={(e) =>
                setStorage(e.target.value as "local" | "s3" | "sftp")
              }
            >
              <option value="local">Local</option>
              <option value="s3">S3-compatible (aws cli)</option>
              <option value="sftp">SFTP (scp)</option>
            </select>
          </div>
          <div className="field">
            <label>Encrypt password (optional)</label>
            <input
              type="password"
              value={encryptPassword}
              onChange={(e) => setEncryptPassword(e.target.value)}
              placeholder="AES-256 if set"
            />
          </div>
        </div>
        <div className="topbar-actions" style={{ flexWrap: "wrap", gap: 12 }}>
          {(
            [
              ["Home / files", incHome, setIncHome],
              ["Databases", incDb, setIncDb],
              ["Mail", incMail, setIncMail],
              ["DNS", incDns, setIncDns]
            ] as const
          ).map(([lab, val, set]) => (
            <label
              key={lab}
              style={{ display: "flex", gap: 6, alignItems: "center" }}
            >
              <input
                type="checkbox"
                checked={val}
                onChange={(e) => set(e.target.checked)}
              />
              {lab}
            </label>
          ))}
        </div>
        <button
          className="btn btn-primary btn-inline"
          type="submit"
          disabled={busy}
          style={{ marginTop: 12 }}
        >
          {busy ? "Working…" : "Run backup now"}
        </button>
      </form>

      <form className="panel-form" onSubmit={savePolicy}>
        <div className="panel-form-title">Backup policy (schedule)</div>
        <div className="form-grid">
          <div className="field">
            <label>Schedule</label>
            <select
              value={polSchedule}
              onChange={(e) =>
                setPolSchedule(e.target.value as typeof polSchedule)
              }
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="manual">Manual only</option>
            </select>
          </div>
          <div className="field">
            <label>Retention count</label>
            <input
              type="number"
              min={1}
              max={50}
              value={polRetention}
              onChange={(e) => setPolRetention(Number(e.target.value))}
            />
          </div>
        </div>
        <p className="card-meta faint">
          Due policies run when the backups list is opened (or staff triggers
          run-scheduled). RPO ≈ schedule interval; RTO ≈ restore minutes for
          small accounts.
        </p>
        <button className="btn btn-ghost btn-inline" type="submit">
          Save policy
        </button>
      </form>

      {policies.length > 0 ? (
        <div className="table-wrap" style={{ marginBottom: 16 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Policy</th>
                <th>Schedule</th>
                <th>Retention</th>
                <th>Next</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td className="mono">{p.schedule}</td>
                  <td className="faint">
                    {p.retentionCount} / {p.retentionDays}d
                  </td>
                  <td className="mono faint" style={{ fontSize: 12 }}>
                    {p.nextRunAt || "—"}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={async () => {
                        await api(`/api/v1/backups/policies/${p.id}`, {
                          method: "DELETE"
                        });
                        await load();
                      }}
                    >
                      Del
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="table-wrap" style={{ marginBottom: 16 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Size</th>
              <th>Status</th>
              <th>Includes</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {archives.length === 0 ? (
              <tr>
                <td colSpan={5} className="faint">
                  No backups yet
                </td>
              </tr>
            ) : (
              archives.map((a) => (
                <tr key={a.id}>
                  <td>
                    <div className="mono">{a.label}</div>
                    <div className="faint" style={{ fontSize: 11 }}>
                      {a.createdAt}
                      {a.encrypted ? " · encrypted" : ""}
                    </div>
                  </td>
                  <td className="mono faint">
                    {a.sizeBytes < 1024
                      ? `${a.sizeBytes} B`
                      : a.sizeBytes < 1024 * 1024
                        ? `${(a.sizeBytes / 1024).toFixed(1)} KB`
                        : `${(a.sizeBytes / (1024 * 1024)).toFixed(1)} MB`}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        a.status === "ok"
                          ? "status-active"
                          : a.status === "failed"
                            ? "status-suspended"
                            : "role-admin"
                      }`}
                    >
                      {a.status}
                    </span>
                  </td>
                  <td className="faint" style={{ fontSize: 12 }}>
                    {[
                      a.includes.home && "home",
                      a.includes.databases && "db",
                      a.includes.mail && "mail",
                      a.includes.dns && "dns"
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </td>
                  <td>
                    <div className="topbar-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setRestoreId(a.id)}
                      >
                        Restore
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => void download(a.id, a.label)}
                      >
                        DL
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={async () => {
                          if (!confirm("Delete this backup?")) return;
                          await api(`/api/v1/backups/${a.id}`, {
                            method: "DELETE"
                          });
                          await load();
                        }}
                      >
                        Del
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {restoreId ? (
        <form className="panel-form" onSubmit={restore}>
          <div className="panel-form-title">Restore wizard</div>
          <p className="card-meta">
            Archive <span className="mono">{restoreId.slice(0, 8)}…</span>
          </p>
          <div className="topbar-actions" style={{ flexWrap: "wrap", gap: 12 }}>
            {(
              [
                ["Home / files", restHome, setRestHome],
                ["Databases only", restDb, setRestDb],
                ["Mail", restMail, setRestMail],
                ["DNS", restDns, setRestDns]
              ] as const
            ).map(([lab, val, set]) => (
              <label
                key={lab}
                style={{ display: "flex", gap: 6, alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  checked={val}
                  onChange={(e) => set(e.target.checked)}
                />
                {lab}
              </label>
            ))}
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <label>Decrypt password (if encrypted)</label>
            <input
              type="password"
              value={decryptPassword}
              onChange={(e) => setDecryptPassword(e.target.value)}
            />
          </div>
          <div className="topbar-actions" style={{ marginTop: 12 }}>
            <button
              className="btn btn-primary btn-inline"
              type="submit"
              disabled={busy}
            >
              Restore selected
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setRestoreId("")}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      <div className="card full" style={{ marginTop: 16 }}>
        <div className="card-label">RPO / RTO expectations</div>
        <ul className="card-meta">
          <li>
            <strong>RPO</strong>: last successful backup (daily policy ≈ 24h).
          </li>
          <li>
            <strong>RTO</strong>: minutes for small accounts on local storage;
            remote restore requires download first.
          </li>
          <li>
            S3: set <span className="mono">REALPANEL_BACKUP_S3_URI</span> + AWS
            credentials. SFTP:{" "}
            <span className="mono">REALPANEL_BACKUP_SFTP_URI</span>.
          </li>
        </ul>
      </div>
    </>
  );
}
